import jsSHA from "jssha/sha256";
import {CountryCode, isSupportedCountry, parsePhoneNumberFromString} from "libphonenumber-js";
import {useEffect} from "react";
import {useLocation, useSearchParams} from "react-router";

import {SIGNUP_QUERY_PARAM} from "@/lib/constants/account.ts";
import {AccountUser} from "@/lib/types/accounts.ts";

import gtm from "../../lib/gtag.ts";

/* a placeholder for most of google events */
export const useGTM = (user?: AccountUser) => {
  // https://reactrouter.com/en/main/hooks/use-location#uselocation
  const location = useLocation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    let userData = {};
    if (user) {
      const {free_trial_expires_at} = user.account;
      userData = {
        user_id: user?.id,
        account_id: user?.account?.id,
        account_type: user?.account?.type,
        user_email: user?.email,
        user_type: free_trial_expires_at ? "freetrial" : "customer",
      };
    }
    gtm.dataLayer({
      dataLayer: {
        event: "pageview_user_panel",
        env: "user_panel",
        ...userData,
      },
    });

    const grmksignup = searchParams.get(SIGNUP_QUERY_PARAM);
    if (grmksignup) {
      gtm.dataLayer({
        dataLayer: {
          event: "profile_created",
          env: "user_panel",
        },
      });
    }

    // TODO: add request_demo_submit event
    // eslint-disable-next-line
  }, [user?.account.id, location]);

  return {
    gtm,
  };
};

/* Normalization rules are dictated by Google Ads Enhanced Conversions:
   https://developers.google.com/google-ads/api/docs/conversions/upload-online?hl=en#email_address_formatting_for_enhanced_conversions */
const GMAIL_DOMAINS = ["gmail.com", "googlemail.com"];

export const normalizeEmail = (email: string) => {
  const [localPart, domain] = email.toLowerCase().replace(/\s+/g, "").split("@");
  if (!localPart || !domain) {
    return "";
  }
  if (!GMAIL_DOMAINS.includes(domain)) {
    return `${localPart}@${domain}`;
  }
  return `${localPart.replace(/\./g, "")}@${domain}`;
};

export const normalizePhone = (phone: string, country?: string) => {
  const defaultCountry = country && isSupportedCountry(country) ? (country as CountryCode) : undefined;
  const parsed = parsePhoneNumberFromString(phone, defaultCountry);
  return parsed?.isValid() ? parsed.format("E.164") : "";
};

export const sha256Hex = (value: string) => {
  const digest = new jsSHA("SHA-256", "TEXT", {encoding: "UTF8"});
  digest.update(value);
  return digest.getHash("HEX");
};

const buildUserData = (user?: AccountUser) => {
  if (!user) {
    return {};
  }
  const email = normalizeEmail(user.email);
  const phone = normalizePhone(user.account.phone, user.account.country);
  return {
    ...(email && {sha256_email_address: sha256Hex(email)}),
    ...(phone && {sha256_phone_number: sha256Hex(phone)}),
  };
};

/* Mirrors the purchase push previously done by uptime/templates/analytics/_google_tag_manager.html,
   which no longer renders since checkout became a client-side route. */
export const trackPurchase = (value: number, billingPeriod: string, coupon = "", user?: AccountUser) => {
  gtm.dataLayer({
    dataLayer: {
      event: "purchase",
      sale_value: value,
      user_data: buildUserData(user),
      ecommerce: {
        currency: "USD",
        value,
        tax: 0,
        shipping: 0,
        transaction_id: window.crypto
          ? window.crypto.getRandomValues(new Uint32Array(4)).join("")
          : Math.random().toString(),
        coupon,
        items: [
          {
            item_id: billingPeriod,
            item_name: billingPeriod,
            price: value,
            quantity: 1,
          },
        ],
      },
    },
  });
};
